import { Component, ChangeDetectionStrategy, DestroyRef, Injector, OnInit, inject, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import type { Observable } from 'rxjs';
import { firstErrorMessage } from '../../forms/formly-errors';
import { FieldShellComponent } from '../../forms/field-shell.component';

type LoadOptionsResult = { value: unknown; label: string }[];
type LoadFn = (ctx: { injector: Injector }) => Observable<LoadOptionsResult>;

@Component({
  selector: 'app-formly-mat-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    TranslateModule,
    FormlyModule,
    FieldShellComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!isHidden()) {
    <app-field-shell
      [label]="props.label || ''"
      [for]="id"
      [required]="!!props.required"
      [hint]="props['hint'] || ''"
      [errorMessage]="isInvalid() ? errorMessage() : ''">
      <mat-form-field appearance="outline" class="mty-flat w-full" [attr.data-field-id]="field.id">
        <mat-select
          [id]="id"
          [formControl]="formControl"
          [formlyAttributes]="field"
          [multiple]="!!props['multiple']"
          [required]="!!props.required"
          [placeholder]="(props.placeholder || '') | translate">
          @if (!!props['multiple']) {
            <mat-select-trigger>
              <div class="flex flex-wrap gap-1">
                @for (opt of selectedOptions(); track $index) {
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-mty-primary/10 text-mty-primary border border-mty-primary font-alternate text-xs font-medium">
                    {{ opt.label | translate }}
                  </span>
                }
              </div>
            </mat-select-trigger>
          }
          @if (isLoading()) {
            <mat-option disabled>
              <span class="inline-flex items-center gap-2">
                <mat-spinner diameter="14"></mat-spinner>
                {{ 'Loading' | translate }}
              </span>
            </mat-option>
          } @else if (selectOptions().length === 0) {
            <mat-option disabled>{{ 'No options available' | translate }}</mat-option>
          } @else {
            @for (opt of selectOptions(); track $index) {
              <mat-option [value]="opt.value">{{ opt.label | translate }}</mat-option>
            }
          }
        </mat-select>
      </mat-form-field>
    </app-field-shell>
    }
  `,
})
export class MatSelectFieldComponent extends FieldType<FieldTypeConfig> implements OnInit {
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  private loadedOptions = signal<LoadOptionsResult | null>(null);
  readonly isLoading = signal(false);

  ngOnInit(): void {
    const loadFn = this.props['loadOptions'] as LoadFn | undefined;
    if (typeof loadFn !== 'function') return;

    const runLoad = (): void => {
      this.isLoading.set(true);
      runInInjectionContext(this.injector, () => {
        loadFn({ injector: this.injector })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (result) => {
              this.loadedOptions.set(result ?? []);
              this.isLoading.set(false);
            },
            error: () => {
              this.loadedOptions.set([]);
              this.isLoading.set(false);
            },
          });
      });
    };

    runLoad();

    const deps = (this.props['dependsOn'] as string[] | undefined) ?? [];
    if (deps.length === 0) return;
    let root: FormlyFieldConfig | undefined = this.field;
    while (root?.parent) root = root.parent;
    for (const depKey of deps) {
      root?.formControl?.get(depKey)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => runLoad());
    }
  }

  selectOptions(): LoadOptionsResult {
    const loaded = this.loadedOptions();
    if (loaded !== null) return loaded;
    return Array.isArray(this.props.options) ? (this.props.options as LoadOptionsResult) : [];
  }

  selectedOptions(): LoadOptionsResult {
    const v = this.formControl.value;
    if (!Array.isArray(v)) return [];
    const byValue = new Map(this.selectOptions().map((o) => [o.value, o]));
    return v.flatMap((val) => (byValue.has(val) ? [byValue.get(val)!] : []));
  }

  isInvalid(): boolean {
    return this.formControl.invalid && (this.formControl.dirty || this.formControl.touched);
  }

  /**
   * When `props.hideWhenEmpty` is true, collapse the field entirely once
   * `loadOptions` resolves to an empty list. Mirrors the legacy
   * `*ngIf="trademarkResults.length > 0"` behaviour for fields like
   * Trademark that only make sense when the backend returns options.
   *
   * While loading (before first resolve) the field stays hidden so it
   * doesn't flash and collapse.
   */
  isHidden(): boolean {
    if (!this.props['hideWhenEmpty']) return false;
    const loaded = this.loadedOptions();
    if (loaded === null) return true;
    return loaded.length === 0;
  }

  errorMessage(): string {
    return firstErrorMessage(this.formControl);
  }
}
